import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
// import { useSelector } from "react-redux";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import NumberInput from "../../framework/NumberInput";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { getCustomerCategories } from "../../services/customer-category-services";
import {
  getCustomerList,
  searchPatients,
} from "../../services/customer-services";
import { isEmpty } from "../../utils/utils";
import {
  debounceCategoryName,
  debounceCustomerName,
  debounceEntityName,
} from "./customerUtils";

const CustomerReportFilter = ({
  reportRef,
  onFilterShow,
  refreshData = false,
  // applyFilters = () => {},
  applyFilterOnMount = true,
}) => {
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerCategoryOptions, setCustomerCategoryOptions] = useState([]);
  const [customerCategory, setCustomerCategory] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [gstinNumber, setGstinNumber] = useState("");
  const [outstanding, setOutstanding] = useState("");
  // const [categoryOptions, setCategoryOptions] = useState([]);

  const [orderBy] = useState([{ fieldName: "patientName", orderType: "asc" }]);
  // const { data } = useSelector((state) => {
  //   return state.commonReducer.tabs[state.commonReducer?.activeTabIndex];
  // });
  const withPhoneCap = ({ value }) => value.length <= 10;
  const apr_filter_customer_name_ref = useRef();
  const apr_filter_customer_category_ref = useRef();
  const applyFilterRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      if (!isEmpty(customerName)) {
        filters.push(
          createFilterParam("patientName", `${customerName}`, "string", LIKE)
        );
      }
      if (!isEmpty(customerCategory)) {
        filters.push(
          createFilterParam(
            "patientCategory",
            `${customerCategory?.id}`,
            "string",
            LIKE
          )
        );
      }

      if (!isEmpty(contactNumber)) {
        filters.push(
          createFilterParam("patientPhone", `${contactNumber}`, "string", LIKE)
        );
      }

      if (!isEmpty(panNumber)) {
        filters.push(
          createFilterParam("panNumber", `${panNumber}`, "string", LIKE)
        );
      }

      if (!isEmpty(gstinNumber)) {
        filters.push(
          createFilterParam("gstin", `${gstinNumber}`, "string", LIKE)
        );
      }
      if (!isEmpty(outstanding)) {
        filters.push(
          createFilterParam("outstanding", `${outstanding}`, "string", LIKE)
        );
      }

      if (orderBy?.length) {
        result.orderBy = orderBy;
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      reportRef.current?.applyFilters(result);
    },
    [
      customerName,
      customerCategory,
      contactNumber,
      panNumber,
      gstinNumber,
      outstanding,
      orderBy,
      reportRef,
    ]
  );

  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_customer_name"
            label="Customer Name"
            ref={apr_filter_customer_name_ref}
            options={customerNameOptions}
            getOptionLabel={(option) => option?.patientName || option}
            isOptionEqualToValue={(option, value) =>
              option?.patientId === value?.patientId
            }
            onFocus={() => setCurrentComponent("apr_filter_customer_name")}
            freeSolo
            onChange={(e, values) => {
              setCustomerName(values?.patientName);
            }}
            value={customerName}
            onInputChange={(e) => {
              if (!e) return;
              setCustomerName(e.target.value);
              setCustomerNameOptions([]);
              if (!e?.target?.value) setCustomerName("");
              if (e.target.value?.length > 0)
                debounceCustomerName(
                  getCustomerList,
                  e.target.value,
                  setCustomerNameOptions
                );
            }}
            markers={JSON.stringify({
              up: "apr_filter_customer_name",
              down: "apr_filter_customer_category",
              right: "apr_filter_customer_category",
              left: "apr_filter_customer_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_customer_category"
            label="Customer Category"
            ref={apr_filter_customer_category_ref}
            options={customerCategoryOptions}
            getOptionLabel={(option) => option.name || option}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onFocus={() => setCurrentComponent("apr_filter_customer_category")}
            freeSolo
            onChange={(e, values) => {
              setCustomerCategory(values);
            }}
            value={customerCategory?.name || ""}
            onInputChange={(e) => {
              if (!e) return;
              setCustomerCategory(e.target.value);
              setCustomerCategoryOptions([]);
              if (!e?.target?.value) setCustomerCategory(null);
              if (e.target.value.length > 0)
                debounceCategoryName(
                  getCustomerCategories,
                  e.target.value,
                  setCustomerCategoryOptions
                );
            }}
            markers={JSON.stringify({
              up: "apr_filter_customer_name",
              down: "apr_filter_contact_number",
              right: "apr_filter_contact_number",
              left: "apr_filter_customer_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="apr_filter_contact_number"
            label="Contact Number"
            allowNegative={false}
            isAllowed={withPhoneCap}
            onFocus={() => {
              setCurrentComponent("apr_filter_contact_number");
            }}
            onValueChange={(values, e) => {
              setContactNumber(values.floatValue);
            }}
            value={contactNumber}
            decimalScale={0}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_customer_category",
                down: "apr_filter_pan_number",
                right: "apr_filter_pan_number",
                left: "apr_filter_customer_category",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_pan_number"
            label="PAN Number"
            value={panNumber}
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.preventDefault();
              }
              if (!/^[A-Za-z0-9]*$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (e.target.value?.length > 10) return;
              setPanNumber(e.target.value.toUpperCase());
            }}
            onFocus={() => {
              setCurrentComponent("apr_filter_pan_number");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_contact_number",
                down: "apr_filter_gstin_number",
                right: "apr_filter_gstin_number",
                left: "apr_filter_contact_number",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_gstin_number"
            label="GSTIN"
            value={gstinNumber}
            onChange={(e) => {
              setGstinNumber(e.target.value);
            }}
            onFocus={() => {
              setCurrentComponent("apr_filter_gstin_number");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_pan_number",
                down: "apr_filter_outstanding",
                right: "apr_filter_outstanding",
                left: "apr_filter_pan_number",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      {/* <FilterGroup>
        <FilterItem>
          <NumberInput
            id="apr_filter_outstanding"
            label="Outstanding"
            allowNegative={false}
            onFocus={() => {
              setCurrentComponent("apr_filter_outstanding");
            }}
            onValueChange={(values, e) => {
              setOutstanding(values.floatValue);
            }}
            value={outstanding}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_gstin_number",
                down: "apr_apply_filter_button",
                right: "apr_apply_filter_button",
                left: "apr_filter_gstin_number",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup> */}
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="apr_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "apr_filter_outstanding",
              left: "apr_filter_outstanding",
              right: "apr_apply_filter_button",
              down: "apr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  useEffect(() => {
    if (onFilterShow) {
      apr_filter_customer_name_ref?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);

  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default CustomerReportFilter;
